/**
 * Pure date computation utilities for the Board Calendar View.
 *
 * All functions are pure — no side effects, no DOM access, no external state.
 * Dates are represented as 'YYYY-MM-DD' strings in local time.
 */

import type { CardSummary, ListSummary } from '../state/active-board-context';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A single cell in the calendar grid. */
export interface GridCell {
  readonly date: string;            // 'YYYY-MM-DD'
  readonly isCurrentMonth: boolean;
  readonly isToday: boolean;
  readonly dayOfMonth: number;
  readonly weekday: number;         // 0=Sun ... 6=Sat
}

/** A complete month grid with 4–6 week rows of 7 cells each. */
export interface MonthGridData {
  readonly year: number;
  readonly month: number;
  readonly weeks: readonly (readonly GridCell[])[];
}

/** A single week grid with exactly 7 cells starting on Sunday. */
export interface WeekGridData {
  readonly weekStartDate: string;
  readonly days: readonly GridCell[];
}

/** A date span for cards with both startDate and dueDate. */
export interface DateSpanData {
  readonly card: CardSummary;
  readonly startDate: string;       // 'YYYY-MM-DD'
  readonly endDate: string;         // 'YYYY-MM-DD'
  readonly listColor: string;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Format a Date object as 'YYYY-MM-DD' in local time. */
function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse a 'YYYY-MM-DD' string into a local Date at midnight. */
function parseDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Get today's date as 'YYYY-MM-DD'. */
function todayStr(): string {
  return toDateStr(new Date());
}

/** Create a GridCell for a given Date, relative to a target month. */
function makeCell(d: Date, targetMonth: number, today: string): GridCell {
  const dateStr = toDateStr(d);
  return {
    date: dateStr,
    isCurrentMonth: d.getMonth() === targetMonth,
    isToday: dateStr === today,
    dayOfMonth: d.getDate(),
    weekday: d.getDay(),
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Generate a month grid with 4–6 week rows of 7 cells each, starting on Sunday.
 * Includes overflow days from adjacent months to fill complete weeks.
 *
 * @param year  Full year (e.g. 2025)
 * @param month 0-indexed month (0=Jan ... 11=Dec)
 */
export function getMonthGrid(year: number, month: number): MonthGridData {
  const today = todayStr();

  // First day of the target month
  const firstOfMonth = new Date(year, month, 1);
  // Start the grid on the Sunday at or before the first of the month
  const startOffset = firstOfMonth.getDay(); // 0=Sun already means start is the 1st
  const gridStart = new Date(year, month, 1 - startOffset);

  // Last day of the target month
  const lastOfMonth = new Date(year, month + 1, 0);
  // End the grid on the Saturday at or after the last of the month
  const endOffset = 6 - lastOfMonth.getDay();
  const gridEnd = new Date(year, month + 1, endOffset);

  const weeks: (readonly GridCell[])[] = [];
  const cursor = new Date(gridStart);

  while (cursor <= gridEnd) {
    const week: GridCell[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(makeCell(new Date(cursor), month, today));
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }

  return { year, month, weeks };
}

/**
 * Generate a week grid with exactly 7 cells starting on Sunday.
 * The grid contains the week that includes the given reference date.
 *
 * @param referenceDate 'YYYY-MM-DD' string
 */
export function getWeekGrid(referenceDate: string): WeekGridData {
  const today = todayStr();
  const ref = parseDate(referenceDate);
  const dayOfWeek = ref.getDay(); // 0=Sun
  const sunday = new Date(ref);
  sunday.setDate(ref.getDate() - dayOfWeek);

  const days: GridCell[] = [];
  const cursor = new Date(sunday);
  const currentMonth = ref.getMonth();

  for (let i = 0; i < 7; i++) {
    days.push(makeCell(new Date(cursor), currentMonth, today));
    cursor.setDate(cursor.getDate() + 1);
  }

  return {
    weekStartDate: toDateStr(sunday),
    days,
  };
}

/**
 * Group cards by their dueDate as 'YYYY-MM-DD' keys.
 * Cards with null dueDate are excluded.
 */
export function groupCardsByDate(
  cards: readonly CardSummary[],
): ReadonlyMap<string, readonly CardSummary[]> {
  const map = new Map<string, CardSummary[]>();

  for (const card of cards) {
    if (card.dueDate === null) continue;
    const dateKey = card.dueDate.slice(0, 10); // 'YYYY-MM-DD'
    const bucket = map.get(dateKey);
    if (bucket) {
      bucket.push(card);
    } else {
      map.set(dateKey, [card]);
    }
  }

  return map;
}

/**
 * Sort cards for a single day cell: by list sort position first (ascending),
 * then by card sort within the same list (ascending).
 */
export function sortCardsForDay(
  cards: readonly CardSummary[],
  lists: readonly ListSummary[],
): readonly CardSummary[] {
  const listSortMap = new Map<string, number>();
  for (const list of lists) {
    listSortMap.set(list.listId, list.sort);
  }

  return [...cards].sort((a, b) => {
    const listSortA = listSortMap.get(a.listId) ?? Number.MAX_SAFE_INTEGER;
    const listSortB = listSortMap.get(b.listId) ?? Number.MAX_SAFE_INTEGER;
    if (listSortA !== listSortB) return listSortA - listSortB;
    return a.sort - b.sort;
  });
}

/**
 * Compute date spans for cards that have both a non-null startDate
 * that is strictly before their non-null dueDate.
 */
export function computeDateSpans(
  cards: readonly CardSummary[],
  lists: readonly ListSummary[],
): readonly DateSpanData[] {
  const listColorMap = new Map<string, string>();
  for (const list of lists) {
    listColorMap.set(list.listId, list.color);
  }

  const spans: DateSpanData[] = [];

  for (const card of cards) {
    if (card.startDate === null || card.dueDate === null) continue;
    const start = card.startDate.slice(0, 10);
    const end = card.dueDate.slice(0, 10);
    if (start >= end) continue; // startDate must be strictly before dueDate

    spans.push({
      card,
      startDate: start,
      endDate: end,
      listColor: listColorMap.get(card.listId) ?? '#6b7280',
    });
  }

  return spans;
}

/**
 * Calculate new dates after a drag-to-reschedule operation.
 * The dueDate moves to the target date. If the card has a startDate,
 * it shifts by the same number of days to preserve the original duration.
 */
export function computeRescheduleDates(
  card: CardSummary,
  targetDate: string,
): { dueDate: string; startDate: string | null } {
  if (card.startDate === null) {
    return { dueDate: targetDate, startDate: null };
  }

  const originalDue = parseDate(card.dueDate!.slice(0, 10));
  const target = parseDate(targetDate);
  const diffMs = target.getTime() - originalDue.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  const originalStart = parseDate(card.startDate.slice(0, 10));
  originalStart.setDate(originalStart.getDate() + diffDays);

  return {
    dueDate: targetDate,
    startDate: toDateStr(originalStart),
  };
}

/**
 * Format a date string for aria-label.
 * Returns full weekday name, full month name, day number, and four-digit year.
 * Example: "Wednesday, January 15, 2025"
 */
export function formatDateForAria(dateStr: string): string {
  const d = parseDate(dateStr);
  const weekdays = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday',
    'Thursday', 'Friday', 'Saturday',
  ];
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  return `${weekdays[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/**
 * Navigate months: add (or subtract) delta months from the given year/month.
 *
 * @param year  Full year
 * @param month 0-indexed month
 * @param delta Number of months to add (negative to subtract)
 */
export function addMonths(
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } {
  const d = new Date(year, month + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
}

/**
 * Navigate weeks: add (or subtract) delta weeks from the given date string.
 *
 * @param dateStr 'YYYY-MM-DD'
 * @param delta   Number of weeks to add (negative to subtract)
 * @returns New date string 'YYYY-MM-DD'
 */
export function addWeeks(dateStr: string, delta: number): string {
  const d = parseDate(dateStr);
  d.setDate(d.getDate() + delta * 7);
  return toDateStr(d);
}

/**
 * Check if two date strings represent the same calendar day.
 */
export function isSameDay(a: string, b: string): boolean {
  return a.slice(0, 10) === b.slice(0, 10);
}

/**
 * Get the number of days between two dates (inclusive of both endpoints).
 */
export function daysBetween(startDate: string, endDate: string): number {
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  const diffMs = end.getTime() - start.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;
}
